import { useContext, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "antd";
import { PlusOutlined, ClockCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import AuthContext from "../../context/AuthContext";
import { useProjects } from "../../queries/projects";
import { useTimeEntries } from "../../queries/timeEntries";
import { useTickets } from "../../queries/tickets";
import { useTasksAcrossProjects } from "../../queries/dashboard";
import { PageShell } from "../../components/ui/PageShell";
import { TaskStatusTag } from "../../components/ui/StatusTag";
import { EmptyState, CardSkeleton } from "../../components/ui/States";
import { TaskProps, TimeEntryProps } from "../../types/interfaces";
import styles from "./dashboard.module.css";

const OPEN: TaskProps["status"][] = ["ready", "in_progress", "in_review"];

const Stat = ({ label, value, note, alert }: { label: string; value: string | number; note?: string; alert?: boolean }) => (
  <div className={styles.stat}>
    <div className="q-kicker">{label}</div>
    <div className={`${styles.statValue} ${alert ? styles.statAlert : ""}`}>{value}</div>
    {note && <div className={styles.statNote}>{note}</div>}
  </div>
);

/**
 * Home. Replaces /projects as the landing page. Same shell for every role —
 * the panels are what change, driven entirely by data the app already fetches.
 */
export const DashboardPage = () => {
  const navigate = useNavigate();
  const { userData } = useContext(AuthContext);
  const isClient = userData?.role === "final_user";

  const { data: projects } = useProjects();
  const { tasks, isLoading, projectOf } = useTasksAcrossProjects(isClient ? [] : projects);

  const weekStart = dayjs().startOf("week");
  const { data: entries } = useTimeEntries({
    userId: userData?._id,
    from: weekStart.format("YYYY-MM-DD"),
    to: dayjs().endOf("week").format("YYYY-MM-DD"),
  });
  const { data: tickets } = useTickets({ solved: false });

  const mine = useMemo(
    () => tasks.filter((t) => t.assigneeId === userData?._id && OPEN.includes(t.status)),
    [tasks, userData]
  );
  const overdue = useMemo(
    () => mine.filter((t) => t.dueDate && dayjs(t.dueDate).isBefore(dayjs(), "day")),
    [mine]
  );

  const byDay = useMemo(() => {
    const days = Array.from({ length: 7 }, (_, i) => weekStart.add(i, "day"));
    // `entries` is `TimeEntryProps[] | undefined`; annotating the fallback
    // keeps it a single concrete array type rather than a
    // `TimeEntryProps[] | never[]` union, which TS can't infer filter/reduce
    // callback parameter types across (surfaces as implicit-any under strict).
    const entryList: TimeEntryProps[] = entries || [];
    return days.map((d) => ({
      day: d,
      minutes: entryList
        .filter((e) => dayjs(e.date).isSame(d, "day"))
        .reduce((n, e) => n + e.durationMinutes, 0),
    }));
  }, [entries, weekStart]);

  const weekMinutes = byDay.reduce((n, d) => n + d.minutes, 0);
  const peak = Math.max(...byDay.map((d) => d.minutes), 60);
  const hours = (m: number) => (m / 60).toFixed(1);

  const greeting = dayjs().hour() < 12 ? "Good morning" : dayjs().hour() < 18 ? "Good afternoon" : "Good evening";

  return (
    <PageShell
      kind="dashboard"
      kicker={dayjs().format("dddd, D MMMM")}
      title={`${greeting}, ${(userData?.name || "").split(" ")[0]}`}
      subtitle={isClient ? "Your open support tickets and the projects we're running for you." : undefined}
      actions={
        <>
          <Button icon={<ClockCircleOutlined />} onClick={() => navigate("/my-time")}>Log time</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate("/support/tickets/new")}>
            New ticket
          </Button>
        </>
      }
    >
      <div className={styles.stats}>
        <Stat label="My open tasks" value={mine.length} note={`across ${new Set(mine.map((t) => t.projectId)).size} projects`} />
        <Stat label="Overdue" value={overdue.length} alert={overdue.length > 0} note={overdue.length ? "needs attention today" : "nothing late"} />
        <Stat label="Logged this week" value={`${hours(weekMinutes)}h`} note={`${hours(weekMinutes / Math.max(dayjs().day(), 1))}h/day average`} />
        <Stat label="Open tickets" value={(tickets || []).length} note="awaiting a reply" />
      </div>

      <div className={styles.grid}>
        <section className={styles.panel}>
          <div className={styles.panelHead}>
            <span className={styles.panelTitle}>My open tasks</span>
            <Button type="link" size="small" className={styles.panelLink} onClick={() => navigate("/projects")}>
              All projects →
            </Button>
          </div>
          {isLoading ? (
            <CardSkeleton height={180} />
          ) : mine.length === 0 ? (
            <EmptyState
              title="Nothing assigned to you"
              description="When someone moves a task to Ready and puts your name on it, it shows up here."
            />
          ) : (
            mine.slice(0, 8).map((t) => (
              <Link key={t._id} className={styles.taskRow} to={`/tasks/${t._id}`}>
                <TaskStatusTag status={t.status} />
                <span className={styles.taskTitle}>{t.title}</span>
                <span className={styles.taskProject}>{projectOf(t)?.name}</span>
              </Link>
            ))
          )}
        </section>

        <aside className={styles.panel}>
          <div className={styles.panelHead}>
            <span className={styles.panelTitle}>This week</span>
            <span className={styles.panelLink + " q-num"} style={{ color: "var(--q-text-faint)" }}>
              {hours(weekMinutes)}h
            </span>
          </div>
          <div className={styles.week}>
            {byDay.map(({ day, minutes }) => (
              <div className={styles.day} key={day.toString()}>
                <div
                  className={`${styles.bar} ${day.isSame(dayjs(), "day") ? styles.barToday : ""}`}
                  style={{ height: `${Math.max((minutes / peak) * 100, 2)}%` }}
                  title={`${hours(minutes)}h`}
                />
                <div className={styles.dayLabel}>{day.format("dd")[0]}</div>
              </div>
            ))}
          </div>
          <div className={styles.quick}>
            <Button block onClick={() => navigate("/my-time")}>Open timesheet</Button>
            <Button block onClick={() => navigate("/support")}>Support queue</Button>
          </div>
        </aside>
      </div>
    </PageShell>
  );
};
